// agentRulesPrefs.js — renderer-side selection of the active agent-rule books (FR, daily-auto-improve).
//
// Source of truth across surfaces is ~/.apolo/agent-rules.json (written via the agentRules IPC), so the
// local daily-improve Node script and the renderer agree. localStorage is the renderer's fast cache,
// hydrated from disk on startup. The Settings → Profile panel writes both; the Code-tab agent reads the
// assembled doctrine via buildRulesInjection() and injects it into the system prompt.

import {
  defaultActiveSlugs, normalizeActive, normalizeVariant, DEFAULT_VARIANT, assembleRulesBlock,
} from '../../../shared/agent-rules/registry.mjs';

const LS_KEY = 'apolo_agent_rules';
export const AGENT_RULES_EVENT = 'apolo:agent-rules-changed';

// window.apolo (Electron preload bridge) isn't in the renderer's lib typings; cast through any.
const w = /** @type {any} */ (typeof window !== 'undefined' ? window : {});

/** Read the current selection (localStorage cache → default core set). Pure-ish, sync. */
export function getActiveRules() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
    if (raw) {
      const p = /** @type {any} */ (JSON.parse(raw));
      return { activeSlugs: normalizeActive(p.activeSlugs), variant: normalizeVariant(p.variant) };
    }
  } catch { /* fall through to defaults */ }
  return { activeSlugs: defaultActiveSlugs(), variant: DEFAULT_VARIANT };
}

/**
 * Persist a selection: localStorage + ~/.apolo mirror (best-effort) + change event.
 * @param {{ activeSlugs?: string[], variant?: string }} [sel]
 */
export function setActiveRules({ activeSlugs, variant } = {}) {
  const sel = { activeSlugs: normalizeActive(activeSlugs), variant: normalizeVariant(variant) };
  try { localStorage.setItem(LS_KEY, JSON.stringify(sel)); } catch { /* quota/private mode */ }
  try { w?.apolo?.agentRules?.set?.(sel); } catch { /* not in Electron */ }
  try { w.dispatchEvent(new CustomEvent(AGENT_RULES_EVENT, { detail: sel })); } catch { /* no window */ }
  return sel;
}

/** On app start, seed the cache from ~/.apolo (the cross-surface source of truth) when present. */
export async function hydrateActiveRules() {
  try {
    const disk = await w?.apolo?.agentRules?.get?.();
    if (disk && Array.isArray(disk.activeSlugs)) {
      const sel = { activeSlugs: normalizeActive(disk.activeSlugs), variant: normalizeVariant(disk.variant) };
      localStorage.setItem(LS_KEY, JSON.stringify(sel));
      w.dispatchEvent(new CustomEvent(AGENT_RULES_EVENT, { detail: sel }));
      return sel;
    }
  } catch { /* ignore */ }
  return null;
}

/** Assemble the doctrine block to inject into a system prompt for the current selection. */
export function buildRulesInjection(budgetChars) {
  const { activeSlugs, variant } = getActiveRules();
  return assembleRulesBlock({ activeSlugs, variant, ...(budgetChars ? { budgetChars } : {}) });
}
